import { useState, useCallback } from 'react';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import {
    StyledHeading,
    StyledArrow,
    StyledDataWrapper,
    StyledHeader,
    StyledHeaderElement,
} from '../../../styles/sharedStyles';
import { StyledHeadingCheckbox } from '../../atoms/SettlementsDoneElement/StyledHeadingCheckbox';
import { StyledText } from '../../atoms/Text/StyledText';
import { StyledDeleteCheckbox } from '../../atoms/UserPanelAddRole/StyledDeleteCheckbox';
import { StyledIsOpenWrapper } from '../../atoms/UserPanelAddRole/StyledIsOpenWrapper';
import { StyledDataWrapper as StyledDataInnerWrapper } from '../../atoms/UserPanelAddRole/StyledDataWrapper';

import CloseIcon from '../../atoms/UserPanelStudens/CloseIcon';
import { getString } from '../../../strings';
import useScrollBar from '../../../logic/hooks/useScrollBar';
import { deleteStudentPreference } from '../../../logic/requests/students';
import { cebulaCompare } from '../../../logic/arrays';
import {
    StyledPreferencesElement,
    StyledPreferencesRowElement,
} from './UserPanelPreferences.styles';
import { hasPermission } from '../../../logic/permissions';

const UserPanelPreferencesElement = ({ user, trainer, preferences, loadStudents, trigger }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { scrollElement, isScrollBar } = useScrollBar(false, true);

    const handleOpen = () => {
        trigger();
        setIsOpen(!isOpen);
    };

    const handleDelete = useCallback(
        (student) => {
            if ([student, trainer].some(({ id }) => isNaN(id))) return;
            if (
                !window.confirm(
                    getString('UserPanelPreferences__delete__confirm_1') +
                        ` ${trainer.lastname} ${trainer.firstname} ` +
                        getString('UserPanelPreferences__delete__confirm_2') +
                        ` ${student.lastname} ${student.firstname}?`
                )
            )
                return;

            deleteStudentPreference(student.id, trainer.id)
                .then(() => loadStudents?.())
                .catch((err) => console.error('handleDelete', err));
        },
        [trainer, loadStudents]
    );

    const sortedPreferences = Object.values(preferences || {}).sort((a, b) => {
        const value = a.value - b.value;
        if (value) return value;
        const lastname = cebulaCompare(a.student.lastname, b.student.lastname);
        if (lastname) return lastname;
        const firstname = cebulaCompare(a.student.firstname, b.student.firstname);
        if (firstname) return firstname;
        return 0;
    });

    return (
        <StyledPreferencesElement isopen={isOpen}>
            <StyledHeading onClick={handleOpen}>
                <StyledText
                    hasdeclaredfontsize="22px"
                    hasdeclaredfontweight="700"
                    hasdeclaredtextalign="center"
                    hasdeclaredtexttransform="uppercase"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredpadding="0 0 0 50px"
                >
                    {trainer.firstname} {trainer.lastname}
                </StyledText>
                <StyledHeadingCheckbox>
                    <StyledArrow isopen={isOpen} />
                </StyledHeadingCheckbox>
            </StyledHeading>
            {isOpen ? (
                <StyledDataWrapper>
                    <ScrollSync>
                        <>
                            <StyledIsOpenWrapper>
                                <ScrollSyncPane>
                                    <StyledHeader $isscrollbar={isScrollBar} ref={scrollElement}>
                                        <div>
                                            <StyledHeaderElement $hasWidth="50%">
                                                <StyledText
                                                    hasdeclaredfontsize="16px"
                                                    hasdeclaredfontweight="700"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredlineheight="1.4em"
                                                >
                                                    {getString('user_panel_preferences_name')}
                                                </StyledText>
                                            </StyledHeaderElement>
                                            <StyledHeaderElement $hasWidth="50%">
                                                <StyledText
                                                    hasdeclaredfontsize="16px"
                                                    hasdeclaredfontweight="700"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredlineheight="1.4em"
                                                >
                                                    {getString(
                                                        'user_panel_preferences_preference_type'
                                                    )}
                                                </StyledText>
                                            </StyledHeaderElement>
                                        </div>
                                    </StyledHeader>
                                </ScrollSyncPane>
                                <StyledDeleteCheckbox isbgcolor />
                            </StyledIsOpenWrapper>
                            <div
                                style={{
                                    marginTop: '5px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '5px',
                                }}
                            >
                                {sortedPreferences.length ? (
                                    sortedPreferences.map((preference) => {
                                        const isForbidden = preference.value === -1;
                                        return (
                                            <StyledPreferencesRowElement
                                                key={preference.student.id}
                                                $minusWidth={50}
                                            >
                                                <ScrollSyncPane>
                                                    <div>
                                                        <StyledDataInnerWrapper
                                                            hasdeclaredbordercolor={isForbidden}
                                                        >
                                                            <StyledHeaderElement $hasWidth="50%">
                                                                <StyledText
                                                                    hasdeclaredfontsize="16px"
                                                                    hasdeclaredfontweight="600"
                                                                    hasdeclaredtextalign="center"
                                                                    hasdeclaredlineheight="1.4em"
                                                                >
                                                                    {preference.student.name}
                                                                </StyledText>
                                                            </StyledHeaderElement>
                                                            <StyledHeaderElement $hasWidth="50%">
                                                                <StyledText
                                                                    hasdeclaredfontsize="16px"
                                                                    hasdeclaredfontweight="600"
                                                                    hasdeclaredtextalign="center"
                                                                    hasdeclaredlineheight="1.4em"
                                                                >
                                                                    {getString(
                                                                        `Trainings_trainings_userPreferences_${
                                                                            isForbidden
                                                                                ? 'Forbidden'
                                                                                : 'Preferred'
                                                                        }`
                                                                    )}
                                                                </StyledText>
                                                            </StyledHeaderElement>
                                                        </StyledDataInnerWrapper>
                                                    </div>
                                                </ScrollSyncPane>
                                                {hasPermission(
                                                    user,
                                                    'can_delete_any_student_preference'
                                                ) && (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '40px',
                                                            minHeight: '100%',
                                                        }}
                                                    >
                                                        <StyledDeleteCheckbox
                                                            onClick={() =>
                                                                handleDelete(preference.student)
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </StyledDeleteCheckbox>
                                                    </div>
                                                )}
                                            </StyledPreferencesRowElement>
                                        );
                                    })
                                ) : (
                                    <StyledText
                                        hasdeclaredfontsize="16px"
                                        hasdeclaredfontweight="700"
                                        hasdeclaredtextalign="center"
                                        hasdeclaredlineheight="1.4em"
                                    >
                                        {getString('user_panel_preferences_no_preferences')}
                                    </StyledText>
                                )}
                            </div>
                        </>
                    </ScrollSync>
                </StyledDataWrapper>
            ) : null}
        </StyledPreferencesElement>
    );
};

export default UserPanelPreferencesElement;
